import React, { useState } from "react";
import { X, Send, Phone, Mail, User, MessageSquare } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PLOT_SIZES = [
  "500 Sq.Mtr", "1000 Sq.Mtr", "2000 Sq.Mtr", "3000 Sq.Mtr",
  "5000 Sq.Mtr", "10000 Sq.Mtr", "Custom Size"
];

export default function EnquiryModal({ isOpen, onClose, selectedPlot }) {
  const { t } = useLang();
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    plot_size: selectedPlot ? `${selectedPlot.size} Sq.Mtr` : "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.message) {
      toast.error("Please fill all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      toast.success("Enquiry submitted! We'll contact you shortly.");
      setForm({ name: "", email: "", phone: "", plot_size: "", message: "" });
      onClose();
    } catch {
      toast.error("Failed to submit. Please call +91-9258959058");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="enquiry-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#1E293B] w-full max-w-md shadow-2xl z-10 border border-white/10">
        <div className="bg-slate-900 px-6 py-4 flex items-center justify-between border-b border-white/10">
          <div>
            <h2 className="text-white text-xl font-bold tracking-wide uppercase"
              style={{ fontFamily: "'Barlow Condensed', sans-serif" }}>
              {t("enquiryModal.title")}
            </h2>
            {selectedPlot && (
              <p className="text-orange-400 text-sm mt-0.5">
                {selectedPlot.size?.toLocaleString("en-IN")} Sq.Mtr — ₹{selectedPlot.price_per_sqmtr?.toLocaleString("en-IN")}/sqmtr
              </p>
            )}
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors" data-testid="enquiry-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1"><User className="inline w-3.5 h-3.5 mr-1" /> {t("enquiryModal.fullName")} *</label>
            <input name="name" value={form.name} onChange={handleChange} placeholder="Your full name" required
              className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500"
              data-testid="enquiry-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1"><Phone className="inline w-3.5 h-3.5 mr-1" /> {t("enquiryModal.phone")} *</label>
            <input name="phone" value={form.phone} onChange={handleChange} placeholder="+91 XXXXX XXXXX" required
              className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500"
              data-testid="enquiry-phone-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1"><Mail className="inline w-3.5 h-3.5 mr-1" /> {t("enquiryModal.email")}</label>
            <input name="email" value={form.email} onChange={handleChange} placeholder="your@email.com" type="email"
              className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500"
              data-testid="enquiry-email-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1">{t("enquiryModal.plotSize")}</label>
            <select name="plot_size" value={form.plot_size} onChange={handleChange}
              className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white"
              data-testid="enquiry-size-select">
              <option value="">{t("enquiryModal.selectSize")}</option>
              {PLOT_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-300 mb-1"><MessageSquare className="inline w-3.5 h-3.5 mr-1" /> {t("enquiryModal.message")} *</label>
            <textarea name="message" value={form.message} onChange={handleChange}
              placeholder="Your enquiry or requirements..." required rows={3}
              className="w-full border border-slate-600 px-3 py-2.5 text-sm focus:outline-none focus:border-orange-500 bg-slate-800 text-white placeholder-slate-500 resize-none"
              data-testid="enquiry-message-input" />
          </div>
          <button type="submit" disabled={submitting}
            className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition-all duration-300 hover:shadow-[0_0_20px_rgba(249,115,22,0.3)] disabled:opacity-60"
            data-testid="enquiry-submit-btn">
            <Send className="w-4 h-4" />
            {submitting ? t("enquiryModal.submitting") : t("enquiryModal.submit")}
          </button>
          <p className="text-xs text-slate-500 text-center">
            {t("enquiryModal.orCall")} <a href="tel:+919258959058" className="text-orange-500 font-medium">+91-9258959058</a>
          </p>
        </form>
      </div>
    </div>
  );
}
